import { API_BASE_URL } from "../constants";

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function jsonOrThrow<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    let detail = "";
    try {
      const body = await response.json();
      detail =
        typeof body?.message === "string"
          ? `: ${body.message}`
          : Array.isArray(body?.message)
            ? `: ${body.message.join(", ")}`
            : "";
    } catch {
      /* body wasn't JSON */
    }
    throw new Error(`${action} failed: ${response.status}${detail}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

/** Row shape for the Frameworks tab. */
export interface FrameworkSummary {
  id: string;
  name: string;
  description: string;
  isGlobal: boolean;
  /** Total referencing rubrics — not scoped to what this caller can read. */
  rubricCount: number;
  canEdit: boolean;
}

/**
 * The narrow shape FrameworkPicker and RubricDetail declare against.
 * FrameworkSummary is a superset, so one listFrameworks call serves both the
 * management tab and the rubric editor's picker.
 */
export interface Framework {
  id: string;
  name: string;
  description: string;
}

/**
 * GET /frameworks/:id. Deliberately NOT extending FrameworkSummary: the
 * single-row read carries `instructions` but no `rubricCount`, which only the
 * list computes.
 */
export interface FrameworkDetail {
  id: string;
  name: string;
  description: string;
  instructions: string;
  isGlobal: boolean;
  canEdit: boolean;
}

export interface FrameworkPayload {
  name: string;
  description: string;
  instructions: string;
  /** Admin-only. Omitted entirely by non-admins and on create. */
  isGlobal?: boolean;
}

export async function listFrameworks(
  token?: string,
): Promise<FrameworkSummary[]> {
  const res = await fetch(`${API_BASE_URL}/frameworks`, {
    headers: authHeaders(token),
  });
  return jsonOrThrow(res, "List frameworks");
}

export async function getFramework(
  id: string,
  token?: string,
): Promise<FrameworkDetail> {
  const res = await fetch(`${API_BASE_URL}/frameworks/${id}`, {
    headers: authHeaders(token),
  });
  return jsonOrThrow(res, "Get framework");
}

export async function createFramework(
  payload: FrameworkPayload,
  token?: string,
): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE_URL}/frameworks`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, "Create framework");
}

export async function updateFramework(
  id: string,
  payload: FrameworkPayload,
  token?: string,
): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE_URL}/frameworks/${id}`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, "Update framework");
}

export async function deleteFramework(
  id: string,
  token?: string,
): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/frameworks/${id}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return jsonOrThrow(res, "Delete framework");
}
